import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { fetchBubblePrompts } from '@/lib/session-api';
import { callCostLabel, exactCount } from '@/lib/session-labels';
import { type SessionConversationBubble, turnCallTokens } from '@matter_extractor/shared';
import { useState } from 'react';
import { CallsTable } from './layer-appels';
import { ServedPrompts } from './layer-brut';
import { TurnLayerShell } from './layer-shell';

/**
 * The two layers a bubble has (prolongation.md §7, issue #186): « Appels et coût »
 * and « Brut ». The three others - Objectifs, Garde-fous et règles, Moteur -
 * read a scribe and a decision, and a bubble has neither (§4.8): the interviewer
 * wrote it, nobody judged it. They are not drawn greyed out, they are not drawn.
 *
 * Both are the turn's layers on the same shell, so a switch of the bar opens them
 * on every bubble as it does on every turn, and the « + » of a bubble reveals
 * them closed.
 */

export interface LayerBubbleProps {
  bubble: SessionConversationBubble;
  open: boolean;
  onToggle: () => void;
}

/**
 * Appels et coût: the ONE call that wrote the bubble, failed or not, with the
 * table of a turn's calls - the model RENDERED, the four buckets of tokens, the
 * duration, the cost - and no running cost of a step, since a page of the thread
 * is not a step and no budget applies to it (§4.7).
 */
export function LayerBubbleAppels({ bubble, open, onToggle }: LayerBubbleProps) {
  const tokens = turnCallTokens(bubble.call);
  const hint = `1 appel · ${callCostLabel(bubble.costEur)} · ${exactCount(tokens)} jetons`;
  return (
    <TurnLayerShell layer="appels" hint={hint} open={open} onToggle={onToggle}>
      <CallsTable
        calls={[bubble.call]}
        tokens={tokens}
        costEur={bubble.costEur}
        totalLabel="Total de la bulle"
      />
      <p className="text-gray-600 text-caption">
        Une page jouée dans la conversation n’est pas une étape : le budget par étape ne s’y
        applique pas, et son coût s’ajoute à celui de la session.
      </p>
    </TurnLayerShell>
  );
}

/**
 * Brut: what the interviewer was served for the bubble - read on a CLICK, from
 * the route of the bubble, at the régime of a turn's served inputs - and the
 * identifiers. There is no scribe output to print, and the layer says why rather
 * than leaving a reader to wonder where it went.
 */
export function LayerBubbleBrut({
  sessionId,
  bubble,
  open,
  onToggle,
}: LayerBubbleProps & { sessionId: string }) {
  const [prompts, setPrompts] = useState(false);
  return (
    <TurnLayerShell
      layer="brut"
      hint="entrée servie à l’interviewer, identifiants"
      open={open}
      onToggle={onToggle}
    >
      <p className="text-gray-600 text-caption">
        Une bulle n’a pas de sortie de scribe : le scribe ne la lit pas, ni le rangeur, ni
        l’analyste.
      </p>

      {prompts ? (
        <ServedPrompts
          queryKey={['session-bubble-prompts', sessionId, bubble.id]}
          load={() => fetchBubblePrompts(sessionId, bubble.id)}
          empty="Cette bulle n’a conservé aucune entrée servie."
        />
      ) : (
        <p className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid={SESSIONS_TEST_IDS.turnPrompts}
            onClick={() => setPrompts(true)}
            className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            Voir l’entrée servie à l’interviewer
          </button>
          <span className="text-gray-500 text-caption">
            chargée à la demande, table{' '}
            <code className="font-mono">session_conversation_bubble_prompts</code>
          </span>
        </p>
      )}

      <p className="break-all font-mono text-gray-500 text-caption">
        bulle {bubble.id} · page {bubble.pageId} · visite {bubble.visitIndex}
      </p>
    </TurnLayerShell>
  );
}
